import React, { useContext, useState, useEffect } from 'react';
import { ArrowForward, WarningFilled } from '@one-thd/sui-icons';
import { Alert, Typography } from '@one-thd/sui-atomic-components';
import { LazyImage } from '../../../shared/LazyImage';
import { getDaysUntil } from '../../../Utils';
import { useIsMobile } from '../../../../hooks';
import { getImageURL } from '../../../../utils/GeneralUtil';
import { DrawerContext, ChallengesContext } from '../../../../contexts';
import { rhDrawer } from '../../../../constants/RewardsHub';
import { ExpireMessage } from '../PerkDrawer/PerkListComponents';
import { ChallengeStepper } from './ChallengeStepper';
export var ChallengeSentence = function (_a) {
    var _b, _c;
    var challenge = _a.challenge;
    if (!challenge) {
        return null;
    }
    var diff = getDaysUntil(challenge.challengeEndDate);
    var stepsLeft = (challenge === null || challenge === void 0 ? void 0 : challenge.challengeStepsTotal) - (challenge === null || challenge === void 0 ? void 0 : challenge.challengeStepsCompleted);
    if (((_c = (_b = challenge === null || challenge === void 0 ? void 0 : challenge.tasks) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.status) !== 'COMPLETE' || !(challenge === null || challenge === void 0 ? void 0 : challenge.tasks)) {
        return (React.createElement("div", { className: "sui-flex" },
            diff < 14 && (React.createElement("span", { className: "sui-pr-2" },
                React.createElement(WarningFilled, { color: "warning" }))),
            React.createElement(ExpireMessage, { numOfDays: diff })));
    }
    return (React.createElement(React.Fragment, null, diff < 14 ? (React.createElement("div", { className: "sui-flex" },
        React.createElement("span", { className: "sui-pr-2" },
            React.createElement(WarningFilled, { color: "warning" })),
        React.createElement(ExpireMessage, { numOfDays: diff }))) : (React.createElement(React.Fragment, null, "".concat(toWord[stepsLeft], " step").concat(stepsLeft < 2 ? '' : 's', " remaining")))));
};
export var ChallengeTile = function (_a) {
    var challenge = _a.challenge, inListDrawer = _a.inListDrawer, isFirstTile = _a.isFirstTile;
    var openDetailsChallenge = useContext(DrawerContext).openDetailsChallenge;
    // Always call useContext hooks at the top level, outside of any conditionals.
    var _b = useContext(ChallengesContext), getChallengesInfo = _b.getChallengesInfo, hasChallengeError = _b.hasChallengeError, hideChallenge = _b.hideChallenge;
    var isMobile = useIsMobile().isMobile;
    var fallBackChallengeImage = getImageURL('ChallengeIcon.svg');
    // State to track if the primary image URL has failed to load.
    var _c = useState(false), imageLoadFailed = _c[0], setImageLoadFailed = _c[1];
    // This effect runs when the component mounts or when the image URL changes.
    useEffect(function () {
        // Reset the failure state whenever a new challenge is passed.
        setImageLoadFailed(false);
        if (challenge === null || challenge === void 0 ? void 0 : challenge.imageUrl) {
            var img = new Image();
            img.src = challenge.imageUrl;
            // If the browser fails to load the image, this event will fire.
            img.onerror = function () {
                setImageLoadFailed(true);
            };
        }
    }, [challenge === null || challenge === void 0 ? void 0 : challenge.imageUrl]); // Dependency array ensures this runs when the URL changes.
    if (!challenge) {
        return null;
    }
    if (hasChallengeError) {
        return (React.createElement("div", { className: "sui-pb-5" },
            React.createElement(Alert, { status: "error" },
                React.createElement("div", null,
                    React.createElement(Typography, null, rhDrawer.challengeErrorMessage),
                    React.createElement("div", { className: "sui-text-base" },
                        React.createElement("button", { type: "button", onClick: function () {
                                getChallengesInfo();
                            } },
                            React.createElement("u", null, "Refresh")))))));
    }
    // This check is now secondary, but still good practice.
    if (challenge.challengeStatus === 'ACTIVE' && !hideChallenge(challenge)) {
        // Determine the correct image source. Use the fallback if the original URL
        // is missing OR if we have detected that it failed to load.
        var imageSrc = imageLoadFailed || !challenge.imageUrl
            ? fallBackChallengeImage
            : challenge.imageUrl;
        return (React.createElement("button", { className: "sui-w-full sui-rounded-base sui-border-1 sui-border-solid sui-border-primary sm:sui-border-0 sui-mb-8", type: "button", 
            // Update onClick to pass the specific challenge data to the details drawer.
            onClick: function () {
                if (openDetailsChallenge) {
                    openDetailsChallenge({ data: challenge, showBack: inListDrawer });
                }
            } },
            React.createElement("div", { className: "sui-w-full sui-flex sui-flex-row sui-justify-between sui-align-center sui-p-4 sm:sui-p-0" },
                React.createElement("div", { className: "sui-w-full sui-flex sui-flex-row sui-justify-start sui-align-start" },
                    React.createElement("div", { className: "sui-min-w-[48px]" },
                        React.createElement(LazyImage, { src: imageSrc, altText: "Challenge-PreEnrolled-Image", width: "48px", height: "48px" })),
                    React.createElement("div", { className: "sui-w-full sui-flex sui-flex-col sui-align-start sui-pr-6 sui-pl-6" },
                        React.createElement("div", { className: "sui-font-bold sui-text-base sm:sui-text-lg sui-text-left" }, challenge === null || challenge === void 0 ? void 0 : challenge.name),
                        React.createElement("div", { className: "sui-text-base sui-text-left sui-pt-4 sui-pb-1 ".concat((challenge === null || challenge === void 0 ? void 0 : challenge.optedStatus) === 'OPTED' && "sui-pb-2") },
                            React.createElement(ChallengeSentence, { challenge: challenge })),
                        React.createElement(ChallengeStepper, { steps: challenge === null || challenge === void 0 ? void 0 : challenge.challengeSteps, inListDrawer: inListDrawer, activeStep: challenge === null || challenge === void 0 ? void 0 : challenge.challengeStepsCompleted }))),
                React.createElement("div", { className: "sui-flex sui-flex-col sui-justify-center sui-align-center" },
                    React.createElement("span", { className: "sui-text-brand" },
                        React.createElement(ArrowForward, { size: isMobile ? 'small' : 'regular' }))))));
    }
    return null; // Explicitly return null if the challenge is not active/visible
};
var toWord = {
    1: 'One',
    2: 'Two',
    3: 'Three',
    4: 'Four',
};
